/**
 * DevExtreme (esm/__internal/ui/text_box/m_text_editor.base.js)
 * Version: 23.2.10
 * Build date: Mon Oct 28 2024
 *
 * Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import config from "../../../core/config";
import devices from "../../../core/devices";
import domAdapter from "../../../core/dom_adapter";
import Guid from "../../../core/guid";
import $ from "../../../core/renderer";
import resizeObserverSingleton from "../../../core/resize_observer";
import {
    Deferred
} from "../../../core/utils/deferred";
import {
    extend
} from "../../../core/utils/extend";
import {
    each
} from "../../../core/utils/iterator";
import {
    getWidth
} from "../../../core/utils/size";
import {
    isDefined
} from "../../../core/utils/type";
import eventsEngine from "../../../events/core/events_engine";
import pointerEvents from "../../../events/pointer";
import {
    addNamespace,
    normalizeKeyName
} from "../../../events/utils/index";
import Editor from "../../../ui/editor/editor";
import LoadIndicator from "../../../ui/load_indicator";
import {
    current,
    isFluent,
    isMaterial
} from "../../../ui/themes";
import {
    focused
} from "../../../ui/widget/selectors";
import errors from "../../../ui/widget/ui.errors";
import ClearButton from "./m_text_editor.clear";
import {
    TextEditorLabel
} from "./m_text_editor.label";
import TextEditorButtonCollection from "./texteditor_button_collection/m_index";
var TEXTEDITOR_CLASS = "dx-texteditor";
var TEXTEDITOR_INPUT_CONTAINER_CLASS = "dx-texteditor-input-container";
var TEXTEDITOR_INPUT_CLASS = "dx-texteditor-input";
var TEXTEDITOR_INPUT_SELECTOR = ".".concat(TEXTEDITOR_INPUT_CLASS);
var TEXTEDITOR_CONTAINER_CLASS = "dx-texteditor-container";
var TEXTEDITOR_BUTTONS_CONTAINER_CLASS = "dx-texteditor-buttons-container";
var TEXTEDITOR_PLACEHOLDER_CLASS = "dx-placeholder";
var TEXTEDITOR_EMPTY_INPUT_CLASS = "dx-texteditor-empty";
var STATE_INVISIBLE_CLASS = "dx-state-invisible";
var TEXTEDITOR_PENDING_INDICATOR_CLASS = "dx-pending-indicator";
var TEXTEDITOR_VALIDATION_PENDING_CLASS = "dx-validation-pending";
var TEXTEDITOR_VALID_CLASS = "dx-valid";
var EVENTS_LIST = ["KeyDown", "KeyPress", "KeyUp", "Change", "Cut", "Copy", "Paste", "Input"];
var CONTROL_KEYS = ["tab", "enter", "shift", "control", "alt", "escape", "pageUp", "pageDown", "end", "home", "leftArrow", "upArrow", "rightArrow", "downArrow"];
var TextEditorLabelCreator = TextEditorLabel;

function checkButtonsOptionType(buttons) {
    if (isDefined(buttons) && !Array.isArray(buttons)) {
        throw errors.Error("E1053")
    }
}
var TextEditorBase = Editor.inherit({
    ctor(_, options) {
        if (options) {
            checkButtonsOptionType(options.buttons)
        }
        this._buttonCollection = new TextEditorButtonCollection(this, this._getDefaultButtons());
        this._$beforeButtonsContainer = null;
        this._$afterButtonsContainer = null;
        this._labelContainerElement = null;
        this.callBase.apply(this, arguments)
    },
    _getDefaultOptions() {
        return extend(this.callBase(), {
            buttons: void 0,
            value: "",
            spellcheck: false,
            showClearButton: false,
            valueChangeEvent: "change",
            placeholder: "",
            inputAttr: {},
            onFocusIn: null,
            onFocusOut: null,
            onKeyDown: null,
            onKeyUp: null,
            onChange: null,
            onInput: null,
            onCut: null,
            onCopy: null,
            onPaste: null,
            onEnterKey: null,
            mode: "text",
            hoverStateEnabled: true,
            focusStateEnabled: true,
            text: void 0,
            displayValueFormatter: value => isDefined(value) && false !== value ? value : "",
            stylingMode: config().editorStylingMode || "outlined",
            showValidationMark: true,
            label: "",
            labelMode: "static",
            labelMark: ""
        })
    },
    _defaultOptionsRules() {
        return this.callBase().concat([{
            device() {
                var themeName = current();
                return isMaterial(themeName)
            },
            options: {
                labelMode: "floating",
                stylingMode: config().editorStylingMode || "filled"
            }
        }, {
            device() {
                var themeName = current();
                return isFluent(themeName)
            },
            options: {
                labelMode: "outside"
            }
        }])
    },
    _getDefaultButtons: () => [{
        name: "clear",
        Ctor: ClearButton
    }],
    _isClearButtonVisible() {
        return this.option("showClearButton") && !this.option("readOnly")
    },
    _input() {
        return this.$element().find(TEXTEDITOR_INPUT_SELECTOR).first()
    },
    _isFocused() {
        return focused(this._input()) || this.callBase()
    },
    _inputWrapper() {
        return this.$element()
    },
    _buttonsContainer() {
        return this._inputWrapper().find(".".concat(TEXTEDITOR_BUTTONS_CONTAINER_CLASS)).eq(0)
    },
    _isControlKey: key => CONTROL_KEYS.includes(key),
    _renderStylingMode() {
        this.callBase();
        this._updateButtonsStyling(this.option("stylingMode"))
    },
    _initMarkup() {
        this.$element().addClass(TEXTEDITOR_CLASS);
        this._renderInput();
        this._renderButtonContainers();
        this._renderStylingMode();
        this._renderInputType();
        this._renderPlaceholder();
        this._renderProps();
        this.callBase();
        this._renderValue();
        this._renderLabel()
    },
    _render() {
        this.callBase();
        this._refreshValueChangeEvent();
        this._refreshEvents();
        this._renderEnterKeyAction();
        this._renderEmptinessEvent()
    },
    _renderInput() {
        this._$textEditorContainer = $("<div>").addClass(TEXTEDITOR_CONTAINER_CLASS).appendTo(this.$element());
        this._$textEditorInputContainer = $("<div>").addClass(TEXTEDITOR_INPUT_CONTAINER_CLASS).appendTo(this._$textEditorContainer);
        this._$textEditorInputContainer.append(this._createInput())
    },
    _getInputContainer() {
        return this._$textEditorInputContainer
    },
    _renderPendingIndicator() {
        this.$element().addClass(TEXTEDITOR_VALIDATION_PENDING_CLASS);
        var $inputContainer = this._getInputContainer();
        var $indicatorElement = $("<div>").addClass(TEXTEDITOR_PENDING_INDICATOR_CLASS).appendTo($inputContainer);
        this._pendingIndicator = this._createComponent($indicatorElement, LoadIndicator)
    },
    _disposePendingIndicator() {
        if (!this._pendingIndicator) {
            return
        }
        this._pendingIndicator.dispose();
        this._pendingIndicator.$element().remove();
        this._pendingIndicator = null;
        this.$element().removeClass(TEXTEDITOR_VALIDATION_PENDING_CLASS)
    },
    _renderValidationState() {
        this.callBase();
        var isPending = "pending" === this.option("validationStatus");
        if (isPending) {
            !this._pendingIndicator && this._renderPendingIndicator();
            this._showValidMark = false
        } else {
            if ("invalid" === this.option("validationStatus")) {
                this._showValidMark = false
            }
            if (!this._showValidMark && true === this.option("showValidationMark")) {
                this._showValidMark = "valid" === this.option("validationStatus") && !!this._pendingIndicator
            }
            this._disposePendingIndicator()
        }
        this._toggleValidMark()
    },
    _getButtonsContainer() {
        return this._$textEditorContainer
    },
    _renderButtonContainers() {
        var buttons = this.option("buttons");
        var $buttonsContainer = this._getButtonsContainer();
        this._$beforeButtonsContainer = this._buttonCollection.renderBeforeButtons(buttons, $buttonsContainer);
        this._$afterButtonsContainer = this._buttonCollection.renderAfterButtons(buttons, $buttonsContainer)
    },
    _cleanButtonContainers() {
        var _a, _b;
        null === (_a = this._$beforeButtonsContainer) || void 0 === _a ? void 0 : _a.remove();
        null === (_b = this._$afterButtonsContainer) || void 0 === _b ? void 0 : _b.remove();
        this._buttonCollection.clean()
    },
    _clean() {
        this._buttonCollection.clean();
        this._disposePendingIndicator();
        this._unobserveLabelContainerResize();
        this._$beforeButtonsContainer = null;
        this._$afterButtonsContainer = null;
        this._$textEditorContainer = null;
        this.callBase()
    },
    _createInput() {
        var $input = $("<input>");
        this._applyInputAttributes($input, this.option("inputAttr"));
        return $input
    },
    _setSubmitElementName(name) {
        var inputAttrName = this.option("inputAttr.name");
        return this.callBase(name || inputAttrName || "")
    },
    _applyInputAttributes($input, customAttributes) {
        var inputAttributes = extend(this._getDefaultAttributes(), customAttributes);
        $input.attr(inputAttributes).addClass(TEXTEDITOR_INPUT_CLASS);
        this._setInputMinHeight($input)
    },
    _setInputMinHeight($input) {
        $input.css("minHeight", this.option("height") ? "0" : "")
    },
    _getPlaceholderAttr() {
        var {
            ios: ios,
            mac: mac
        } = devices.real();
        var {
            placeholder: placeholder
        } = this.option();
        var value = placeholder || (ios || mac ? " " : null);
        return value
    },
    _getDefaultAttributes() {
        var defaultAttributes = {
            autocomplete: "off",
            placeholder: this._getPlaceholderAttr()
        };
        return defaultAttributes
    },
    _updateButtons(names) {
        this._buttonCollection.updateButtons(names)
    },
    _updateButtonsStyling(editorStylingMode) {
        each(this.option("buttons"), (_, _ref) => {
            var {
                options: options,
                name: buttonName
            } = _ref;
            if (options && !options.stylingMode && this.option("visible")) {
                var buttonInstance = this.getButton(buttonName);
                buttonInstance.option && buttonInstance.option("stylingMode", "underlined" === editorStylingMode ? "text" : "contained")
            }
        })
    },
    _renderValue() {
        var renderInputPromise = this._renderInputValue();
        return renderInputPromise.promise()
    },
    _renderInputValue(value) {
        value = null !== value && void 0 !== value ? value : this.option("value");
        var text = this.option("text");
        var displayValue = this.option("displayValue");
        var displayValueFormatter = this.option("displayValueFormatter");
        if (void 0 !== displayValue && null !== value) {
            text = displayValueFormatter(displayValue)
        } else if (!isDefined(text)) {
            text = displayValueFormatter(value)
        }
        this.option("text", text);
        if (this._input().val() !== (isDefined(text) ? text : "")) {
            this._renderDisplayText(text)
        } else {
            this._toggleEmptinessEventHandler()
        }
        return Deferred().resolve()
    },
    _renderDisplayText(text) {
        this._input().val(text);
        this._toggleEmptinessEventHandler()
    },
    _isValueValid() {
        if (this._input().length) {
            var {
                validity: validity
            } = this._input().get(0);
            if (validity) {
                return validity.valid
            }
        }
        return true
    },
    _toggleEmptiness(isEmpty) {
        this.$element().toggleClass(TEXTEDITOR_EMPTY_INPUT_CLASS, isEmpty);
        this._togglePlaceholder(isEmpty)
    },
    _togglePlaceholder(isEmpty) {
        this.$element().find(".".concat(TEXTEDITOR_PLACEHOLDER_CLASS)).eq(0).toggleClass(STATE_INVISIBLE_CLASS, !isEmpty)
    },
    _renderProps() {
        this._toggleReadOnlyState();
        this._toggleSpellcheckState();
        this._toggleTabIndex()
    },
    _toggleDisabledState(value) {
        this.callBase.apply(this, arguments);
        var $input = this._input();
        $input.prop("disabled", value)
    },
    _toggleTabIndex() {
        var $input = this._input();
        var disabled = this.option("disabled");
        var focusStateEnabled = this.option("focusStateEnabled");
        if (disabled || !focusStateEnabled) {
            $input.attr("tabIndex", -1)
        } else {
            $input.removeAttr("tabIndex")
        }
    },
    _toggleReadOnlyState() {
        this._input().prop("readOnly", this._readOnlyPropValue());
        this.callBase()
    },
    _readOnlyPropValue() {
        return this.option("readOnly")
    },
    _toggleSpellcheckState() {
        this._input().prop("spellcheck", this.option("spellcheck"))
    },
    _unobserveLabelContainerResize() {
        if (this._labelContainerElement) {
            resizeObserverSingleton.unobserve(this._labelContainerElement);
            this._labelContainerElement = null
        }
    },
    _getLabelContainer() {
        return this._input()
    },
    _getLabelContainerWidth() {
        return getWidth(this._getLabelContainer())
    },
    _getLabelBeforeWidth() {
        var buttonsBeforeWidth = this._$beforeButtonsContainer && getWidth(this._$beforeButtonsContainer);
        return null !== buttonsBeforeWidth && void 0 !== buttonsBeforeWidth ? buttonsBeforeWidth : 0
    },
    _updateLabelWidth() {
        this._label.updateBeforeWidth(this._getLabelBeforeWidth());
        this._label.updateMaxWidth(this._getLabelContainerWidth())
    },
    _getFieldElement() {
        return this._getLabelContainer()
    },
    _setFieldAria(force) {
        var _a;
        var inputAttr = this.option("inputAttr");
        var ariaLabel = null === inputAttr || void 0 === inputAttr ? void 0 : inputAttr["aria-label"];
        var labelId = null === (_a = this._label) || void 0 === _a ? void 0 : _a.getId();
        var value = ariaLabel ? void 0 : labelId;
        if (value || force) {
            var aria = {
                labelledby: value,
                label: ariaLabel
            };
            this.setAria(aria, this._getFieldElement())
        }
    },
    _renderLabel() {
        this._unobserveLabelContainerResize();
        this._labelContainerElement = $(this._getLabelContainer()).get(0);
        var {
            label: label,
            labelMode: labelMode,
            labelMark: labelMark,
            rtlEnabled: rtlEnabled
        } = this.option();
        var labelConfig = {
            onClickHandler: () => {
                this.focus()
            },
            onHoverHandler: e => {
                e.stopPropagation()
            },
            onActiveHandler: e => {
                e.stopPropagation()
            },
            $editor: this.$element(),
            text: label,
            mark: labelMark,
            mode: labelMode,
            rtlEnabled: rtlEnabled,
            containsButtonsBefore: !!this._$beforeButtonsContainer,
            getContainerWidth: () => this._getLabelContainerWidth(),
            getBeforeWidth: () => this._getLabelBeforeWidth()
        };
        this._label = new TextEditorLabelCreator(labelConfig);
        this._setFieldAria();
        if (this._labelContainerElement) {
            resizeObserverSingleton.observe(this._labelContainerElement, this._updateLabelWidth.bind(this))
        }
    },
    _renderPlaceholder() {
        this._renderPlaceholderMarkup();
        this._attachPlaceholderEvents()
    },
    _renderPlaceholderMarkup() {
        if (this._$placeholder) {
            this._$placeholder.remove();
            this._$placeholder = null
        }
        var $input = this._input();
        var placeholder = this.option("placeholder");
        var placeholderAttributes = {
            id: placeholder ? "dx-".concat(new Guid) : void 0,
            "data-dx_placeholder": placeholder
        };
        var $placeholder = this._$placeholder = $("<div>").attr(placeholderAttributes);
        $placeholder.insertAfter($input);
        $placeholder.addClass(TEXTEDITOR_PLACEHOLDER_CLASS)
    },
    _attachPlaceholderEvents() {
        var startEvent = addNamespace(pointerEvents.up, this.NAME);
        eventsEngine.on(this._$placeholder, startEvent, () => {
            eventsEngine.trigger(this._input(), "focus")
        });
        this._toggleEmptinessEventHandler()
    },
    _placeholder() {
        return this._$placeholder || $()
    },
    _clearValueHandler(e) {
        var $input = this._input();
        e.stopPropagation();
        this._saveValueChangeEvent(e);
        this._clearValue();
        !this._isFocused() && eventsEngine.trigger($input, "focus");
        eventsEngine.trigger($input, "input")
    },
    _clearValue() {
        this.clear()
    },
    _renderEvents() {
        var $input = this._input();
        each(EVENTS_LIST, (_, event) => {
            if (this.hasActionSubscription("on".concat(event))) {
                var action = this._createActionByOption("on".concat(event), {
                    excludeValidators: ["readOnly"]
                });
                eventsEngine.on($input, addNamespace(event.toLowerCase(), this.NAME), e => {
                    if (this._disposed) {
                        return
                    }
                    action({
                        event: e
                    })
                })
            }
        })
    },
    _refreshEvents() {
        var $input = this._input();
        each(EVENTS_LIST, (_, event) => {
            eventsEngine.off($input, addNamespace(event.toLowerCase(), this.NAME))
        });
        this._renderEvents()
    },
    _keyPressHandler() {
        this.option("text", this._input().val())
    },
    _keyDownHandler(e) {
        var $input = this._input();
        var isCtrlEnter = e.ctrlKey && "enter" === normalizeKeyName(e);
        var isNewValue = $input.val() !== this.option("value");
        if (isCtrlEnter && isNewValue) {
            eventsEngine.trigger($input, "change")
        }
    },
    _getValueChangeEventOptionName: () => "valueChangeEvent",
    _renderValueChangeEvent() {
        var keyPressEvent = addNamespace(this._renderValueEventName(), "".concat(this.NAME, "TextChange"));
        var valueChangeEvent = addNamespace(this.option(this._getValueChangeEventOptionName()), "".concat(this.NAME, "ValueChange"));
        var keyDownEvent = addNamespace("keydown", "".concat(this.NAME, "TextChange"));
        var $input = this._input();
        eventsEngine.on($input, keyPressEvent, this._keyPressHandler.bind(this));
        eventsEngine.on($input, valueChangeEvent, this._valueChangeEventHandler.bind(this));
        eventsEngine.on($input, keyDownEvent, this._keyDownHandler.bind(this))
    },
    _cleanValueChangeEvent() {
        var valueChangeNamespace = ".".concat(this.NAME, "ValueChange");
        var textChangeNamespace = ".".concat(this.NAME, "TextChange");
        eventsEngine.off(this._input(), valueChangeNamespace);
        eventsEngine.off(this._input(), textChangeNamespace)
    },
    _refreshValueChangeEvent() {
        this._cleanValueChangeEvent();
        this._renderValueChangeEvent()
    },
    _renderValueEventName: () => "input change keypress",
    _focusTarget() {
        return this._input()
    },
    _focusEventTarget() {
        return this.element()
    },
    _isInput(element) {
        return element === this._input().get(0)
    },
    _preventNestedFocusEvent(event) {
        if (event.isDefaultPrevented()) {
            return true
        }
        var shouldPrevent = this._isNestedTarget(event.relatedTarget);
        if ("focusin" === event.type) {
            shouldPrevent = shouldPrevent && this._isNestedTarget(event.target) && !this._isInput(event.target)
        } else if (!shouldPrevent) {
            this._toggleFocusClass(false, this.$element())
        }
        shouldPrevent && event.preventDefault();
        return shouldPrevent
    },
    _isNestedTarget(target) {
        return !!this.$element().find(target).length
    },
    _focusClassTarget() {
        return this.$element()
    },
    _focusInHandler(event) {
        this._preventNestedFocusEvent(event);
        this.callBase.apply(this, arguments)
    },
    _focusOutHandler(event) {
        this._preventNestedFocusEvent(event);
        this.callBase.apply(this, arguments)
    },
    _toggleFocusClass(isFocused, $element) {
        this.callBase(isFocused, this._focusClassTarget($element))
    },
    _hasFocusClass(element) {
        return this.callBase($(element || this.$element()))
    },
    _renderEmptinessEvent() {
        var $input = this._input();
        eventsEngine.on($input, "input blur", this._toggleEmptinessEventHandler.bind(this))
    },
    _toggleEmptinessEventHandler() {
        var text = this._input().val();
        var isEmpty = ("" === text || null === text) && this._isValueValid();
        this._toggleEmptiness(isEmpty)
    },
    _valueChangeEventHandler(e, formattedValue) {
        if (this.option("readOnly")) {
            return
        }
        this._saveValueChangeEvent(e);
        this.option("value", arguments.length > 1 ? formattedValue : this._input().val());
        this._saveValueChangeEvent(void 0)
    },
    _renderEnterKeyAction() {
        this._enterKeyAction = this._createActionByOption("onEnterKey", {
            excludeValidators: ["readOnly"]
        });
        eventsEngine.off(this._input(), "keyup.onEnterKey.dxTextEditor");
        eventsEngine.on(this._input(), "keyup.onEnterKey.dxTextEditor", this._enterKeyHandlerUp.bind(this))
    },
    _enterKeyHandlerUp(e) {
        if (this._disposed) {
            return
        }
        if ("enter" === normalizeKeyName(e)) {
            this._enterKeyAction({
                event: e
            })
        }
    },
    _updateValue() {
        this._options.silent("text", null);
        this._renderValue()
    },
    _dispose() {
        this._enterKeyAction = void 0;
        this.callBase()
    },
    _getSubmitElement() {
        return this._input()
    },
    _hasActiveElement() {
        return this._input().is(domAdapter.getActiveElement(this._input()[0]))
    },
    _optionChanged(args) {
        var {
            name: name,
            fullName: fullName,
            value: value
        } = args;
        var eventName = name.replace("on", "");
        if (EVENTS_LIST.includes(eventName)) {
            this._refreshEvents();
            return
        }
        switch (name) {
            case "valueChangeEvent":
                this._refreshValueChangeEvent();
                this._refreshFocusEvent();
                this._refreshEvents();
                break;
            case "onValueChanged":
                this._createValueChangeAction();
                break;
            case "focusStateEnabled":
                this.callBase(args);
                this._toggleTabIndex();
                break;
            case "spellcheck":
                this._toggleSpellcheckState();
                break;
            case "mode":
                this._renderInputType();
                break;
            case "onEnterKey":
                this._renderEnterKeyAction();
                break;
            case "placeholder":
                this._renderPlaceholder();
                this._setFieldAria(true);
                this._input().attr({
                    placeholder: this._getPlaceholderAttr()
                });
                break;
            case "label":
                this._label.updateText(value);
                this._setFieldAria(true);
                break;
            case "labelMark":
                this._label.updateMark(value);
                break;
            case "labelMode":
                this._label.updateMode(value);
                this._setFieldAria();
                break;
            case "width":
                this.callBase(args);
                this._label.updateMaxWidth(this._getLabelContainerWidth());
                break;
            case "readOnly":
            case "disabled":
                this._updateButtons();
                this.callBase(args);
                break;
            case "showClearButton":
                this._updateButtons(["clear"]);
                break;
            case "text":
                break;
            case "value":
                this._updateValue();
                this.callBase(args);
                break;
            case "inputAttr":
                this._applyInputAttributes(this._input(), this.option(name));
                break;
            case "stylingMode":
                this._renderStylingMode();
                this._updateLabelWidth();
                break;
            case "buttons":
                if (fullName === name) {
                    checkButtonsOptionType(value)
                }
                this._cleanButtonContainers();
                this._renderButtonContainers();
                this._updateButtonsStyling(this.option("stylingMode"));
                this._updateLabelWidth();
                this._label.updateContainsButtonsBefore(!!this._$beforeButtonsContainer);
                break;
            case "visible":
                this.callBase(args);
                if (value && this.option("buttons")) {
                    this._cleanButtonContainers();
                    this._renderButtonContainers();
                    this._updateButtonsStyling(this.option("stylingMode"))
                }
                break;
            case "displayValueFormatter":
                this._invalidate();
                break;
            case "showValidationMark":
                break;
            default:
                this.callBase(args)
        }
    },
    _renderInputType() {
        this._setInputType(this.option("mode"))
    },
    _setInputType(type) {
        var input = this._input();
        if ("search" === type) {
            type = "text"
        }
        try {
            input.prop("type", type)
        } catch (e) {
            input.prop("type", "text")
        }
    },
    getButton(name) {
        return this._buttonCollection.getButton(name)
    },
    focus() {
        eventsEngine.trigger(this._input(), "focus")
    },
    clear() {
        if (this._showValidMark) {
            this._showValidMark = false;
            this._renderValidationState()
        }
        var defaultOptions = this._getDefaultOptions();
        if (this.option("value") === defaultOptions.value) {
            this._options.silent("text", "");
            this._renderValue()
        } else {
            this.option("value", defaultOptions.value)
        }
    },
    _resetToInitialValue() {
        if (this.option("value") === this._initialValue) {
            this._options.silent("text", this._initialValue);
            this._renderValue()
        } else {
            this.callBase()
        }
        this._disposePendingIndicator();
        this._showValidMark = false;
        this._toggleValidMark()
    },
    _toggleValidMark() {
        this.$element().toggleClass(TEXTEDITOR_VALID_CLASS, !!this._showValidMark)
    },
    reset() {
        var value = arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : void 0;
        if (arguments.length) {
            this.callBase(value)
        } else {
            this.callBase()
        }
    },
    on(eventName, eventHandler) {
        var result = this.callBase(eventName, eventHandler);
        var event = eventName.charAt(0).toUpperCase() + eventName.substr(1);
        if (EVENTS_LIST.includes(event)) {
            this._refreshEvents()
        }
        return result
    }
});
export default TextEditorBase;
